/**
 * Client-side error monitoring (Sentry). No-ops entirely when VITE_SENTRY_DSN is unset, so
 * this is safe to ship before a Sentry project/DSN exists — see .env.example for setup notes.
 *
 * Deliberately minimal: error capture only, no session replay or performance tracing (both can
 * capture on-screen content/PII and aren't needed for "is anything actually breaking").
 */
import * as Sentry from '@sentry/react';
import { PROJECT_ID, MODE } from '../buildInfo';

let initialized = false;

export function initErrorMonitoring(): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn?.trim()) {
    return;
  }
  initialized = true;
  Sentry.init({
    dsn: dsn.trim(),
    environment: PROJECT_ID || MODE,
    // No BrowserTracing/Replay added — just the SDK's default error-capture integrations
    // (window.onerror, unhandledrejection, etc.), no performance tracing or session replay.
    tracesSampleRate: 0,
    // Firebase's own error retry/backoff produces a lot of transient network noise — filter
    // it out at the SDK level rather than letting it drown out real bugs in the dashboard.
    ignoreErrors: ['Firebase: Error (auth/network-request-failed)', 'ResizeObserver loop'],
  });
}

/** Report a caught error (e.g. from a React error boundary) with optional extra context. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
